import { Icon } from "@app/icons";
import { Button, VisuallyHidden } from "@mantine/core";
import { formatNumber } from "@ui/format";
import { Fragment, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

/** The pieces of a table's tools line that every table shows alike: a ``DataTable``'s and the trial data grid's. */

/** A heading's sort mark: ↕ when it does not sort, ▲ / ▼ when it does (design.css ``.sort-mark``). */
export const SORT_MARK = { none: "↕", ascending: "▲", descending: "▼" } as const;

/** Whether the filter boxes under the headings show: while filters are in force, unless the person closed them. */
export function useFilterRow(filtered: number): [boolean, (show: boolean) => void] {
  const [open, setOpen] = useState<boolean>();
  return [open ?? filtered > 0, setOpen];
}

/** The Filter rows button: shows or hides the filter boxes; says how many filters are in force. */
export function FilterRowButton(props: { open: boolean; filtered: number; onToggle: (show: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Button
      size="compact-sm"
      variant={props.open || props.filtered ? "light" : "default"}
      leftSection={<Icon name="filter" size={15} />}
      aria-pressed={props.open}
      title={t("dataTable.filterHelp")}
      onClick={() => props.onToggle(!props.open)}
    >
      {props.filtered ? t("dataTable.filterActive", { count: props.filtered }) : t("dataTable.filter")}
    </Button>
  );
}

/** The table as the page shows it at first: the person's column order, widths, hidden columns, sort and filters gone.
 *  Disabled while there is nothing to undo. */
export function ResetTableButton({ disabled, onReset }: { disabled?: boolean; onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      size="compact-sm"
      variant="default"
      leftSection={<Icon name="undo" size={15} />}
      title={t("dataTable.resetTableHelp")}
      disabled={disabled}
      onClick={onReset}
    >
      {t("dataTable.resetTable")}
    </Button>
  );
}

export type SortName = { name: string; descending: boolean };

/** "Sorted by Name ▲, Year ▼" and the button that gives the rows' own order back; nothing when nothing sorts. */
export function SortNote({ keys, onClear }: { keys: readonly SortName[]; onClear: () => void }) {
  const { t } = useTranslation();
  if (!keys.length) return null;
  return (
    <span className="dt-state">
      {t("dataTable.sortedBy")}{" "}
      {keys.map((k, i) => (
        <Fragment key={k.name}>
          {i > 0 && ", "}
          <b>{k.name}</b> {k.descending ? SORT_MARK.descending : SORT_MARK.ascending}
          <VisuallyHidden>{t(k.descending ? "dataTable.descending" : "dataTable.ascending")}</VisuallyHidden>
        </Fragment>
      ))}
      <Button size="compact-sm" variant="default" onClick={onClear}>
        {t("dataTable.clearSort")}
      </Button>
    </span>
  );
}

/** "12 of 340 rows shown" and Clear filters (``children``: further buttons before it). */
export function ShownNote(props: { shown: number; total: number; onClear: () => void; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className="dt-state" role="status">
      {t("dataTable.shown", { shown: formatNumber(props.shown), total: formatNumber(props.total) })}
      {props.children}
      <Button size="compact-sm" variant="default" onClick={props.onClear}>
        {t("dataTable.clearFilters")}
      </Button>
    </span>
  );
}
